import React, { useEffect, useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function WhatToExpectSection() {
  const [activeStep, setActiveStep] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const headerReveal = useScrollReveal({
    threshold: 0.1
  });
  // Data
  const steps = [{
    period: '24-48 HOURS',
    title: 'Absorption & Activation',
    desc: "Notice subtle grounded calm & focus. Some feel nothing yet and that's normal. Trust the process."
  }, {
    period: '1-2 WEEKS',
    title: 'The Shield Goes Up',
    desc: "You notice you're not as run down. That constant low-grade vulnerability starts lifting. Your body feels like it's finally working with you — defending you — instead of leaving you exposed."
  }, {
    period: '1 MONTH',
    title: 'Steady Resilience',
    desc: 'You finish tasks. You remember things. Your present with your kids, life purpose and with god.'
  }, {
    period: '3 MONTHS',
    title: 'The New Normal',
    desc: "The chaos isn't gone life is still life. But you're different & feel whole. The stress fades because you're finally showing up as the warrior God designed you to be."
  }];
  // Auto-advance logic
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % steps.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, steps.length]);
  return <section className="relative w-full min-h-[700px] lg:h-[850px] flex flex-col justify-center overflow-hidden font-sans bg-[#0f1a13]" data-id="element-535">
      {/* === 1. CINEMATIC VIDEO BACKGROUND === */}
      <div className="absolute inset-0 z-0" data-id="element-536">
        <img src="https://cdn.shopify.com/s/files/1/0637/8940/0115/files/ezgif-41ade0920902232a.gif?v=1764234157" alt="Olive Leaf Capsule Technology" className="w-full h-full object-cover object-center blur-[3px]" data-id="element-537" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(15,26,19,0.1)_0%,rgba(15,26,19,0.4)_100%)]" data-id="element-538"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-full flex flex-col justify-center py-12 lg:py-0" data-id="element-539">
        {/* === HEADER === */}
        <div ref={headerReveal.ref} className={`text-center mb-10 lg:mb-14 transition-all duration-1000 ${headerReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-540">
          <h2 className="text-4xl sm:text-5xl lg:text-7xl font-serif text-transparent bg-clip-text bg-gradient-to-b from-white via-[#fffaf0] to-[#eaddcf] drop-shadow-[0_4px_20px_rgba(0,0,0,0.3)] tracking-tight pb-3 leading-tight" data-id="element-541">
            What to Expect
          </h2>

          {/* Elegant Divider */}
          <div className="flex items-center justify-center gap-4 mt-2 opacity-90" data-id="element-542">
            <div className="h-[1px] w-8 sm:w-16 bg-gradient-to-r from-transparent to-[#c9a962]" data-id="element-543"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-[#c9a962] shadow-[0_0_15px_#c9a962]" data-id="element-544"></div>
            <div className="h-[1px] w-8 sm:w-16 bg-gradient-to-l from-transparent to-[#c9a962]" data-id="element-545"></div>
          </div>
        </div>

        {/* === CENTERED CONTENT === */}
        <div className="w-full flex justify-center" data-id="element-546">
          <div className="w-full max-w-3xl bg-[#0f1a13]/40 backdrop-blur-xl border border-white/10 rounded-[2rem] p-5 sm:p-8 lg:p-12 shadow-2xl ring-1 ring-white/5" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-547">
            <div className="flex flex-col gap-4 lg:gap-6" data-id="element-548">
              {steps.map((step, index) => {
              const isActive = activeStep === index;
              return <div key={index} onClick={() => setActiveStep(index)} className={`relative pl-5 sm:pl-6 lg:pl-8 py-3 sm:py-5 cursor-pointer group rounded-xl transition-all duration-500 ${isActive ? 'bg-white/10 shadow-lg scale-[1.01]' : 'hover:bg-white/5'}`} data-id="element-549">
                    {/* Active Progress Bar */}
                    {isActive && <div className="absolute left-0 top-3 bottom-3 w-[3px] bg-[#c9a962] rounded-r-full overflow-hidden shadow-[0_0_15px_rgba(201,169,98,0.6)]" data-id="element-550">
                        {!isPaused && <div className="absolute top-0 left-0 w-full h-full bg-[#fff5d6] animate-[progress_5s_linear] origin-top" data-id="element-551"></div>}
                      </div>}

                    {/* Content */}
                    <div className={`transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-60 group-hover:opacity-90'}`} data-id="element-552">
                      <div className="flex justify-between items-baseline mb-1 lg:mb-2" data-id="element-553">
                        <span className={`text-[10px] sm:text-xs lg:text-sm font-bold tracking-[0.2em] uppercase ${isActive ? 'text-[#c9a962]' : 'text-white/70'}`} data-id="element-554">
                          {step.period}
                        </span>
                      </div>

                      <h3 className={`font-serif text-white mb-1 sm:mb-2 transition-all duration-300 ${isActive ? 'text-lg sm:text-2xl lg:text-3xl drop-shadow-sm' : 'text-base sm:text-xl lg:text-2xl'}`} data-id="element-555">
                        {step.title}
                      </h3>

                      {/* Description Expansion */}
                      <div className={`grid transition-all duration-500 ease-in-out ${isActive ? 'grid-rows-[1fr] opacity-100 mt-2 sm:mt-3' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-556">
                        <div className="overflow-hidden" data-id="element-557">
                          <p className="text-gray-100 text-sm sm:text-base lg:text-lg leading-relaxed font-light max-w-2xl" data-id="element-558">
                            {step.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>

      <style data-id="element-559">{`
        @keyframes progress {
          from { height: 0%; }
          to { height: 100%; }
        }
      `}</style>
    </section>;
}